/**
 * Client-side thumbnail generation utilities for images and PDFs.
 * Uses browser-image-compression for images and pdfjs-dist for PDFs.
 * Both libraries use Web Workers for non-blocking performance.
 */

import imageCompression from "browser-image-compression";
import * as pdfjsLib from "pdfjs-dist";

// Configure PDF.js worker - uses the bundled worker from pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

/** Thumbnail configuration */
const THUMBNAIL_CONFIG = {
  /** Maximum width/height for thumbnails in pixels */
  MAX_SIZE: 200,
  /** JPEG quality for thumbnail output (0-1) */
  JPEG_QUALITY: 0.8,
  /** Output MIME type for thumbnails */
  OUTPUT_TYPE: "image/jpeg",
} as const;

/**
 * Generate a thumbnail from an image file.
 * Uses browser-image-compression which runs in a Web Worker.
 *
 * @param file - Image file to generate thumbnail from
 * @returns Base64-encoded JPEG thumbnail (without data: prefix)
 */
export async function generateImageThumbnail(file: File): Promise<string> {
  const options = {
    maxWidthOrHeight: THUMBNAIL_CONFIG.MAX_SIZE,
    useWebWorker: true,
    fileType: THUMBNAIL_CONFIG.OUTPUT_TYPE,
    initialQuality: THUMBNAIL_CONFIG.JPEG_QUALITY,
  };

  const compressedBlob = await imageCompression(file, options);
  return blobToBase64(compressedBlob);
}

/**
 * Generate a thumbnail from a PDF file by rendering the first page.
 * Uses pdfjs-dist which runs in a Web Worker.
 *
 * @param file - PDF file to generate thumbnail from
 * @returns Base64-encoded JPEG thumbnail (without data: prefix)
 */
export async function generatePdfThumbnail(file: File): Promise<string> {
  // Convert file to ArrayBuffer for pdf.js
  const arrayBuffer = await file.arrayBuffer();

  // Load the PDF document
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;

  // Get the first page
  const page = await pdf.getPage(1);

  // Calculate scale to fit within thumbnail size while maintaining aspect ratio
  const viewport = page.getViewport({ scale: 1 });
  const scale = Math.min(
    THUMBNAIL_CONFIG.MAX_SIZE / viewport.width,
    THUMBNAIL_CONFIG.MAX_SIZE / viewport.height
  );
  const scaledViewport = page.getViewport({ scale });

  // Create an offscreen canvas
  const canvas = document.createElement("canvas");
  canvas.width = Math.floor(scaledViewport.width);
  canvas.height = Math.floor(scaledViewport.height);

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Failed to get canvas 2D context");
  }

  // Render the page to canvas
  await page.render({
    canvasContext: context,
    viewport: scaledViewport,
    canvas,
  }).promise;

  // Convert canvas to JPEG base64
  const dataUrl = canvas.toDataURL(
    THUMBNAIL_CONFIG.OUTPUT_TYPE,
    THUMBNAIL_CONFIG.JPEG_QUALITY
  );

  // Clean up
  pdf.destroy();

  // Return base64 without the data:image/jpeg;base64, prefix
  return dataUrl.split(",")[1];
}

/**
 * Helper to convert Blob to base64 string (without data: prefix)
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
